/**
 * The wire contract, expressed as Zod schemas (spec §7).
 *
 * Every request and response shape is generated: `generated/zod.gen.ts` comes
 * from `apps/api/openapi.json`, which comes from the API's records. Each schema
 * is `z<Record>` with a matching `<Record>` type, named as on the server. To
 * change one, change the C# record, refresh the snapshot, and run
 * `pnpm api:generate` — never edit the generated file.
 *
 * This file adds only what the server does not describe: client names for two
 * shapes used across the app, and the push subscription input, which is
 * stricter on the client than the server's flat request record.
 *
 * Unknown fields are ignored, never interpreted (§7.4): the generated objects
 * are plain `z.object`, which strips them.
 */
import { z } from 'zod'
import { zItemPayload, type SubmittedResponse } from './generated/zod.gen'

export * from './generated/zod.gen'

/** One item as served. `content`, `correctAnswer` and `explanation` are narrowed per type in `items.ts`. */
export const apiItemSchema = zItemPayload

export type ApiItem = z.infer<typeof apiItemSchema>

/** One response as it goes over the wire. `id` is the client-generated idempotency key (§7.3). */
export type OutgoingResponse = SubmittedResponse

/**
 * Mirrors what `PushSubscription.toJSON()` produces, so the client posts the
 * browser's own object rather than transcribing it into a flatter shape first —
 * one fewer place for a typo that surfaces only as a push nobody receives.
 *
 * The server's `SubscribeRequest` is one flat record with every field optional
 * and checks the combination itself; this union states the same rule up front.
 *
 * The two transports are not interchangeable and the split is not a preference.
 * Android's WebView has no `PushManager`, so the Capacitor build cannot use Web
 * Push; the PWA cannot use FCM without the Firebase JS SDK.
 */
export const pushSubscriptionInputSchema = z.discriminatedUnion('transport', [
  z.object({
    transport: z.literal('webpush'),
    endpoint: z.string().min(1),
    keys: z.object({ p256dh: z.string(), auth: z.string() }),
    label: z.string().optional(),
  }),
  z.object({
    transport: z.literal('fcm'),
    /** An FCM registration token — a device handle, not a URL. */
    token: z.string().min(1),
    label: z.string().optional(),
  }),
])

export type PushSubscriptionInput = z.infer<typeof pushSubscriptionInputSchema>
